import React, { useEffect, useState } from "react";
import { Flame, Trophy } from "lucide-react";
import { api } from "../../lib/api";
import Avatar from "./Avatar";

/**
 * TopCampersRail — a small social-proof + retention component.
 *
 * Surfaces the top contributors of the last `windowDays` (default 30) on the
 * Community page. Score is computed server-side: posts + replies*0.5 +
 * kudos_received*2. Showing avatars + names + scores gives recognisable
 * faces visibility, builds reputation, and gently encourages contribution
 * (people want to be seen on the board).
 *
 * Silently hides itself when fewer than 2 campers qualify — keeps it from
 * looking lonely on a quiet community.
 */
export default function TopCampersRail({ windowDays = 30, limit = 5 }) {
  const [campers, setCampers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/community/top-campers", {
          params: { window_days: windowDays, limit },
        });
        if (!cancelled) setCampers(data.campers || []);
      } catch {
        if (!cancelled) setCampers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [windowDays, limit]);

  if (loading || campers.length < 2) return null;

  const windowLabel =
    windowDays >= 365 ? "all time" :
    windowDays >= 28 ? "this month" :
    `last ${windowDays} days`;

  return (
    <section
      className="mb-8 border-2 hairline-strong bg-gradient-to-br from-[#fff4ee] to-white p-5 sm:p-6"
      data-testid="top-campers-rail"
    >
      <header className="flex items-center gap-2 mb-4">
        <Trophy className="w-5 h-5 text-[#ff4500]" strokeWidth={2} />
        <h3 className="font-display text-lg sm:text-xl">Top campers · {windowLabel}</h3>
        <span className="ml-auto text-[10px] mono uppercase tracking-wider text-[#888]">
          Most active contributors
        </span>
      </header>
      <ol className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="top-campers-list">
        {campers.map((c, i) => (
          <li
            key={c.user_id}
            className="relative p-3 bg-white border hairline flex items-center gap-3"
            data-testid={`top-camper-${i}`}
          >
            <div className="relative shrink-0">
              <Avatar src={c.picture} name={c.user_name} size={44} />
              <span
                className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center text-white ${
                  i === 0 ? "bg-[#ff4500]" : i === 1 ? "bg-[#444]" : "bg-[#888]"
                }`}
                aria-label={`Rank ${i + 1}`}
              >
                {i + 1}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display text-sm truncate" title={c.user_name}>{c.user_name}</div>
              <div className="text-[11px] mono text-[#666] flex items-center gap-1 mt-0.5">
                <Flame className="w-3 h-3 text-[#ff4500]" strokeWidth={2.5} />
                <span>{c.score} pts</span>
                <span className="opacity-50">·</span>
                <span>{c.posts}p</span>
                {c.replies > 0 && <><span className="opacity-50">·</span><span>{c.replies}r</span></>}
                {c.kudos_received > 0 && <><span className="opacity-50">·</span><span>{c.kudos_received}♥</span></>}
              </div>
              {c.badge && (
                <div className="text-[9px] mono uppercase tracking-wider text-[#ff4500] mt-1 truncate">
                  {c.badge}
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}
